import React, { useState, useEffect, useContext } from 'react';
import { Container, Form, Button, Card, Row, Col, Alert } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import api from '../../services/api';
import './User.css';

const UserForm = () => {
  const { userId } = useParams(); // 如果是編輯模式，會有userId
  const { user: currentUser } = useContext(AuthContext);
  const navigate = useNavigate();
  
  // 表單狀態
  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [role, setRole] = useState(1); // 預設為客戶角色
  const [phone, setPhone] = useState('');
  const [inactive, setInactive] = useState(0); // 預設為啟用
  
  // 頁面狀態
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // 是否為編輯模式
  const isEditMode = !!userId;
  
  // 確保只有管理員可以訪問
  useEffect(() => {
    if (currentUser?.role !== 0) {
      navigate('/');
    }
  }, [currentUser, navigate]);
  
  // 如果是編輯模式，載入用戶資料
  useEffect(() => {
    if (isEditMode && currentUser?.role === 0) {
      loadUserData();
    }
  }, [isEditMode, currentUser]);
  
  const loadUserData = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/users/${userId}`);
      const userData = response.data.data;
      
      setLoginId(userData.login_id);
      setRole(userData.role);
      setPhone(userData.phone || '');
      setInactive(userData.inactive);
      
      // 在編輯模式下不要自動填充密碼字段
      setPassword('');
      setConfirmPassword('');
      
      setError('');
    } catch (err) {
      console.error('載入用戶資料失敗:', err);
      setError('載入用戶資料失敗，請稍後再試');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 表單驗證
    if (!loginId) {
      setError('請輸入登入ID');
      return;
    }
    
    if (!isEditMode && !password) {
      setError('請輸入密碼');
      return;
    }
    
    if (password && password !== confirmPassword) {
      setError('兩次輸入的密碼不匹配');
      return;
    }
    
    if (password && password.length < 6) {
      setError('密碼長度至少為6個字符');
      return;
    }
    
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      const userData = {
        login_id: loginId,
        role: parseInt(role, 10),
        phone,
        inactive: parseInt(inactive, 10)
      };
      
      // 只有當密碼字段有值時才包含密碼
      if (password) {
        userData.password = password;
      }
      
      if (isEditMode) {
        // 更新現有用戶
        await api.put(`/users/${userId}`, userData);
        setSuccess('用戶已成功更新');
      } else {
        // 創建新用戶
        await api.post('/users', userData);
        setSuccess('用戶已成功創建');
        
        // 清空表單
        if (!isEditMode) {
          setLoginId('');
          setPassword('');
          setConfirmPassword('');
          setRole(1);
          setPhone('');
          setInactive(0);
        }
      }
    } catch (err) {
      console.error('保存用戶失敗:', err);
      setError('保存用戶失敗: ' + (err.response?.data?.message || '請稍後再試'));
    } finally {
      setSaving(false);
    }
  };
  
  const handleCancel = () => {
    navigate('/users');
  };
  
  return (
    <Container className="user-form-container">
      <h2 className="mb-4">{isEditMode ? '編輯用戶' : '新增用戶'}</h2>
      
      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}
      
      {loading ? (
        <div className="text-center my-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">載入中...</span>
          </div>
        </div>
      ) : (
        <Card>
          <Card.Body>
            <Form onSubmit={handleSubmit}>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>登入ID</Form.Label>
                    <Form.Control 
                      type="text" 
                      value={loginId} 
                      onChange={(e) => setLoginId(e.target.value)}
                      disabled={isEditMode} // 編輯模式下不允許修改登入ID
                      required
                    />
                  </Form.Group>
                </Col>
                
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>角色</Form.Label>
                    <Form.Select 
                      value={role} 
                      onChange={(e) => setRole(e.target.value)}
                    >
                      <option value={0}>管理員</option>
                      <option value={1}>客戶</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{isEditMode ? '新密碼' : '密碼'}</Form.Label>
                    <Form.Control 
                      type="password" 
                      value={password} 
                      onChange={(e) => setPassword(e.target.value)}
                      required={!isEditMode} // 創建新用戶時必填
                    />
                    {isEditMode && (
                      <Form.Text className="text-muted">
                        留空表示不更改密碼
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>
                
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>確認密碼</Form.Label>
                    <Form.Control 
                      type="password" 
                      value={confirmPassword} 
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required={!isEditMode || password !== ''} // 如果輸入了新密碼則必填
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>電話</Form.Label>
                    <Form.Control 
                      type="text" 
                      value={phone} 
                      onChange={(e) => setPhone(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>狀態</Form.Label>
                    <Form.Select 
                      value={inactive} 
                      onChange={(e) => setInactive(e.target.value)}
                    >
                      <option value={0}>啟用</option>
                      <option value={1}>停用</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              
              <div className="d-flex justify-content-between mt-4">
                <Button 
                  variant="outline-secondary" 
                  onClick={handleCancel}
                  disabled={saving}
                >
                  取消
                </Button>
                
                <Button 
                  variant="primary" 
                  type="submit" 
                  disabled={saving}
                >
                  {saving ? '保存中...' : '保存'}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
};

export default UserForm;